/**
 * Deterministic PRNG utilities for the N8N mock layer.
 *
 * Why deterministic: a volume should always yield the SAME dataset for a given
 * campaign/lead seed. Non-deterministic Math.random() would reshuffle ids,
 * names and statuses on every re-render — cards would flicker, selected leads
 * would lose identity, and the UI would be impossible to demo consistently.
 *
 * mulberry32 is a tiny, fast, well-distributed seedable PRNG. The seed comes
 * from the parent object's index (campaign #3 → seed 3), so "some" and "huge"
 * share the same first 3 campaigns and the same first leads within them.
 */

export type Rng = () => number;

/** Build a seeded PRNG from a numeric seed. Returns a 0..1 float generator. */
export const mulberry32 = (seed: number): Rng => {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/** Hash an arbitrary string into a stable 32-bit seed. */
export const hashSeed = (input: string): number => {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

/** Build a PRNG from any string key (campaign id, lead id, etc.). */
export const rngFor = (key: string | number): Rng =>
  mulberry32(typeof key === 'number' ? key : hashSeed(key));

/** Pick a deterministic element from a pool. */
export const pick = <T>(pool: readonly T[], rng: Rng): T => pool[Math.floor(rng() * pool.length) % pool.length];

/** Integer in [min, max] inclusive. */
export const intBetween = (min: number, max: number, rng: Rng): number =>
  min + Math.floor(rng() * (max - min + 1));

/** Float in [min, max). */
export const floatBetween = (min: number, max: number, rng: Rng): number =>
  min + rng() * (max - min);

/** True with the given probability (0..1). */
export const chance = (probability: number, rng: Rng): boolean => rng() < probability;

/**
 * Weighted pick. `entries` is [value, weight][]. Weights are relative.
 * Used for the realistic funnel status distribution (many new leads, few
 * converted) so the Kanban shows every column without manual tuning.
 */
export const weightedPick = <T>(entries: readonly (readonly [T, number])[], rng: Rng): T => {
  const total = entries.reduce((sum, [, w]) => sum + w, 0);
  let roll = rng() * total;
  for (const [value, weight] of entries) {
    roll -= weight;
    if (roll <= 0) return value;
  }
  return entries[entries.length - 1][0];
};

/** Take N deterministic items from a pool (may repeat). */
export const pickMany = <T>(pool: readonly T[], count: number, rng: Rng): T[] =>
  Array.from({ length: count }, () => pick(pool, rng));
